import React from "react";
import { modelPricesCreate, modelPricesUpdate } from "../../../../api/api";
import { useModelPrices } from "../../../../state/api/ai/useModelPrices";
import { Doc, ModelPrice } from "../../../../types";
import Button from "../../buttons/Button";
import Submit from "../../buttons/Submit";
import FieldWithLabel from "../../formik/FieldWithLabel";
import Icon from "../../icons/Icon";
import { Dialog, DialogBody, DialogFooter, ModalForm } from "../../modal/ModalForm";

interface Props {
  price?: Doc<ModelPrice>
}

export default function EditPriceButton(props: Props) {

  const { price } = props;

  return (
    <Dialog
      dialog={<PriceEditForm {...props} />}
      trigger={<Button text={price ? "Edit" : "New"} icon={price ? "pen" : "plus"} />}
    />
  )
}

function PriceEditForm(props: Props) {

  const {
    reloadModelPrices
  } = useModelPrices();

  const {
    price
  } = props;

  return (
    <ModalForm
      title="Edit Price"
      description="Price per million tokens"
      initialValues={{
        label: price?.label ?? "",
        ppmInputTokens: price?.ppmInputTokens ?? 0,
        ppmInputTokensCacheWrite5m: price?.ppmInputTokensCacheWrite5m ?? 0,
        ppmInputTokensCacheWrite1h: price?.ppmInputTokensCacheWrite1h ?? 0,
        ppmInputTokensCacheRead: price?.ppmInputTokensCacheRead ?? 0,
        ppmOutputTokens: price?.ppmOutputTokens ?? 0
      } satisfies ModelPrice}
      onSubmit={async (values, apiCall) => {
        const payload: ModelPrice = {
          ...values,
          ppmInputTokens: Number(values.ppmInputTokens),
          ppmInputTokensCacheWrite5m: Number(values.ppmInputTokensCacheWrite5m),
          ppmInputTokensCacheWrite1h: Number(values.ppmInputTokensCacheWrite1h),
          ppmInputTokensCacheRead: Number(values.ppmInputTokensCacheRead),
          ppmOutputTokens: Number(values.ppmOutputTokens)
        };

        const res = await apiCall(
          price
            ? modelPricesUpdate({ ...payload, _id: price._id })
            : modelPricesCreate(payload)
        );

        if (!res.success) {
          return false;
        }

        await reloadModelPrices();
        return true;
      }}
    >
      {
        formik => {

          const i = (l: string) => (
            <div className="d-flex flex-row align-items-center gap-1">
              <Icon icon="info-circle" size={10} />
              <span className="font-code font-xs">
                {l}
              </span>
            </div>
          )

          return (
            <>
              <DialogBody>
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex flex-column w-100 gap-1">
                    <FieldWithLabel
                      name="label"
                      label="Label"
                      placeholder="e.g. Claude Sonnet 4.6"
                    />
                    {i("Give the tier a name so it is recognisable when assigning it to a model.")}
                  </div>
                  <FieldWithLabel
                    type="number"
                    step="0.01"
                    min="0"
                    name="ppmInputTokens"
                    label="Base Input Tokens ($ / MTok)"
                  />
                  <div className="d-flex flex-row gap-3 w-100">
                    <FieldWithLabel
                      className="w-100"
                      type="number"
                      step="0.01"
                      min="0"
                      name="ppmInputTokensCacheWrite5m"
                      label="5m Cache Writes ($ / MTok)"
                    />
                    <FieldWithLabel
                      className="w-100"
                      type="number"
                      step="0.01"
                      min="0"
                      name="ppmInputTokensCacheWrite1h"
                      label="1h Cache Writes ($ / MTok)"
                    />
                  </div>
                  <FieldWithLabel
                    type="number"
                    step="0.01"
                    min="0"
                    name="ppmInputTokensCacheRead"
                    label="Cache Hits & Refreshes ($ / MTok)"
                  />
                  <FieldWithLabel
                    type="number"
                    step="0.01"
                    min="0"
                    name="ppmOutputTokens"
                    label="Output Tokens ($ / MTok)"
                  />
                </div>
              </DialogBody>
              <DialogFooter>
                <Submit />
              </DialogFooter>
            </>
          )
        }
      }
    </ModalForm>
  )
}
